import React from 'react';
import { Ban, Check, Download, Receipt, Send, X } from 'lucide-react';
import type { NotaFiscal } from '../../../shared/types';
import { NF_STATUS_META, formatCompetencia, formatCurrency, formatDate, lastChange } from '../constants';

export interface NFTableActions {
  onDownloadPDF: (nota: NotaFiscal) => void;
  /** Colaborador: enviar (liberada) ou reenviar (rejeitada). */
  onEnviar?: (nota: NotaFiscal) => void;
  /** Financeiro. */
  onAprovar?: (nota: NotaFiscal) => void;
  onRejeitar?: (nota: NotaFiscal) => void;
  onFaturar?: (nota: NotaFiscal) => void;
  onCancelar?: (nota: NotaFiscal) => void;
}

interface Props extends NFTableActions {
  notas: NotaFiscal[];
  /** Mostra a coluna de colaborador (visão do financeiro). */
  showCollaborator?: boolean;
  emptyMessage?: React.ReactNode;
}

const th = 'px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500';
const td = 'px-4 py-3 text-sm text-gray-900';

function ActionButton({
  onClick,
  label,
  icon,
  tone,
}: {
  onClick: () => void;
  label: string;
  icon: React.ReactNode;
  tone: 'blue' | 'green' | 'red' | 'violet' | 'gray';
}) {
  const tones = {
    blue: 'bg-blue-600 text-white hover:bg-blue-700',
    green: 'border border-emerald-300 text-emerald-700 hover:bg-emerald-50',
    red: 'border border-red-300 text-red-700 hover:bg-red-50',
    violet: 'border border-violet-300 text-violet-700 hover:bg-violet-50',
    gray: 'border border-gray-300 text-gray-600 hover:bg-gray-50',
  };
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-medium ${tones[tone]}`}
    >
      {icon}
      {label}
    </button>
  );
}

export const NFTable: React.FC<Props> = ({
  notas,
  showCollaborator = false,
  emptyMessage = 'Nenhuma nota fiscal encontrada.',
  onDownloadPDF,
  onEnviar,
  onAprovar,
  onRejeitar,
  onFaturar,
  onCancelar,
}) => (
  <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="border-b border-gray-200 bg-gray-50">
          <tr>
            <th className={th}>Competência</th>
            {showCollaborator && <th className={th}>Colaborador</th>}
            <th className={th}>Número da NF</th>
            <th className={th}>Valor</th>
            <th className={th}>Status</th>
            <th className={th}>Última mudança</th>
            <th className={th}>Ações</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {notas.map((nota) => {
            const meta = NF_STATUS_META[nota.status];
            return (
              <tr key={nota.id} className="align-top hover:bg-gray-50">
                <td className={`${td} whitespace-nowrap font-medium`}>{formatCompetencia(nota.referenceMonth)}</td>
                {showCollaborator && <td className={td}>{nota.collaboratorName ?? '—'}</td>}
                <td className={`${td} whitespace-nowrap`}>{nota.number ?? <span className="text-gray-400">—</span>}</td>
                <td className={`${td} whitespace-nowrap`}>{formatCurrency(nota.value)}</td>
                <td className={td}>
                  <span className={`inline-block rounded-full px-2 py-1 text-xs font-medium ${meta?.badge ?? 'bg-gray-100 text-gray-700'}`}>
                    {meta?.label ?? nota.status}
                  </span>
                  {nota.status === 'rejeitada' && nota.rejectionReason && (
                    <p className="mt-1 max-w-xs text-xs text-red-700">Motivo: {nota.rejectionReason}</p>
                  )}
                </td>
                <td className={`${td} whitespace-nowrap text-gray-600`}>{formatDate(lastChange(nota))}</td>
                <td className={td}>
                  <div className="flex flex-wrap items-center gap-1.5">
                    {onEnviar && nota.status === 'liberada' && (
                      <ActionButton tone="blue" icon={<Send className="h-3.5 w-3.5" />} label="Enviar NF" onClick={() => onEnviar(nota)} />
                    )}
                    {onEnviar && nota.status === 'rejeitada' && (
                      <ActionButton tone="blue" icon={<Send className="h-3.5 w-3.5" />} label="Reenviar NF" onClick={() => onEnviar(nota)} />
                    )}
                    {onAprovar && nota.status === 'em_analise' && (
                      <ActionButton tone="green" icon={<Check className="h-3.5 w-3.5" />} label="Aprovar" onClick={() => onAprovar(nota)} />
                    )}
                    {onRejeitar && nota.status === 'em_analise' && (
                      <ActionButton tone="red" icon={<X className="h-3.5 w-3.5" />} label="Rejeitar" onClick={() => onRejeitar(nota)} />
                    )}
                    {onFaturar && nota.status === 'aprovada' && (
                      <ActionButton tone="violet" icon={<Receipt className="h-3.5 w-3.5" />} label="Faturar" onClick={() => onFaturar(nota)} />
                    )}
                    {onCancelar && nota.status === 'liberada' && (
                      <ActionButton tone="gray" icon={<Ban className="h-3.5 w-3.5" />} label="Cancelar liberação" onClick={() => onCancelar(nota)} />
                    )}
                    {nota.pdfUrl && (
                      <button
                        type="button"
                        onClick={() => onDownloadPDF(nota)}
                        className="rounded p-1 text-emerald-600 hover:bg-emerald-50 hover:text-emerald-900"
                        title="Ver PDF"
                        aria-label={`Ver PDF da nota ${nota.number ?? ''}`}
                      >
                        <Download className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {notas.length === 0 && <div className="py-10 text-center text-sm text-gray-500">{emptyMessage}</div>}
    </div>
  </div>
);
